import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../config/environment';
import MarkdownToolbar from './MarkdownToolbar';
import { useRef } from 'react';

export default function CreateEventForm({ onSuccess, onCancel }) {
  const descriptionRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    type: 'networking_breakfast',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    timezone: 'America/New_York',
    location_city: 'Charlotte',
    location_name: 'KGOB Charlotte Office',
    location_address: '456 Trade Street, Charlotte, NC 28202',
    parking_instructions: '',
    capacity: '',  // Empty string allows blank (unlimited)
    blocked_seats: 0,
    requires_rsvp: true,
    allow_guests: true,
    max_guests_per_rsvp: 1,
    visibility: 'draft',
    enable_donation_link: false,
    donation_link_url: '',
    donation_link_text: 'Support This Cause'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Batch state updates to prevent re-render interrupting typing
    if (name === 'title') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      
      setFormData(prev => ({
        ...prev,
        title: value,
        slug: slug
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('admin_token');
    
    try {
      const eventData = {
        ...formData,
        // Send separate date/time to backend
        start_date: formData.start_date,
        start_time: formData.start_time,
        end_date: formData.end_date,
        end_time: formData.end_time,
        timezone: formData.timezone,
        capacity: formData.capacity === '' ? null : parseInt(formData.capacity),
        blocked_seats: parseInt(formData.blocked_seats || 0),
        max_guests_per_rsvp: parseInt(formData.max_guests_per_rsvp || 1),
        allow_guests: formData.allow_guests,
        enable_donation_link: formData.enable_donation_link,
        donation_link_url: formData.enable_donation_link ? formData.donation_link_url : null,
        donation_link_text: formData.enable_donation_link ? formData.donation_link_text : 'Support This Cause'
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });

      const result = await response.json(); // Read response once
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to create event');
      }

      onSuccess(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onCancel} data-lenis-prevent>
      <div 
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-8 pb-4 border-b border-[#E1DFD7]">
          <h2 className="font-heading text-2xl font-semibold text-[#0B2240]">Create New Event</h2>
          <button onClick={onCancel} className="text-[#4C5A70] hover:text-[#0B2240]">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-8 py-6 space-y-6"
            style={{ maxHeight: 'calc(85vh - 140px)' }}
          >
          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Event Title *</label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]"
              placeholder="Business Networking Breakfast" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">URL Slug *</label>
            <input type="text" name="slug" value={formData.slug} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]"
              placeholder="business-networking-breakfast-jan-2026" />
            <p className="text-xs text-[#4C5A70] mt-1">URL: /events/{formData.slug}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Description *</label>
            <MarkdownToolbar 
              textareaRef={descriptionRef}
              onInsert={(newValue) => setFormData(prev => ({ ...prev, description: newValue }))}
            />
            <textarea 
              ref={descriptionRef}
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              required 
              rows={6}
              className="w-full px-4 py-3 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3] font-mono text-sm"
              placeholder="Join us for...&#10;&#10;**Highlights:**&#10;- Feature 1&#10;- Feature 2&#10;&#10;*Questions? Contact us!*" 
            />
            <p className="text-xs text-[#4C5A70] mt-1">Use toolbar buttons or type markdown directly</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Event Type *</label>
              <select name="type" value={formData.type} onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
                <option value="networking_breakfast">Networking Breakfast</option>
                <option value="free_coffee_friday">Free Coffee Friday</option>
                <option value="charity">Charity Event</option>
                <option value="client_event">Client Event</option>
                <option value="workshop">Workshop</option>
                <option value="special">Special Event</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Visibility *</label>
              <select name="visibility" value={formData.visibility} onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Start Date *</label>
              <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Start Time *</label>
              <input type="time" name="start_time" value={formData.start_time} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">End Date *</label>
              <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">End Time *</label>
              <input type="time" name="end_time" value={formData.end_time} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Location *</label>
            <select name="location_city" value={formData.location_city} onChange={handleChange}
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
              <option value="Charlotte">Charlotte</option>
              <option value="Mooresville">Mooresville</option>
              <option value="Winston-Salem">Winston-Salem</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Timezone *</label>
            <select name="timezone" value={formData.timezone} onChange={handleChange}
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
              <option value="America/New_York">Eastern (ET) - NC, NY</option>
              <option value="America/Chicago">Central (CT) - TX, IL</option>
              <option value="America/Denver">Mountain (MT) - CO, AZ</option>
              <option value="America/Los_Angeles">Pacific (PT) - CA, WA</option>
            </select>
            <p className="text-xs text-[#4C5A70] mt-1">Times will display in this timezone for attendees</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Venue Name *</label>
            <input type="text" name="location_name" value={formData.location_name} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Address *</label>
            <input type="text" name="location_address" value={formData.location_address} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Parking Instructions (optional)</label>
            <input type="text" name="parking_instructions" value={formData.parking_instructions} onChange={handleChange}
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]"
              placeholder="Free parking in rear lot" />
            <p className="text-xs text-[#4C5A70] mt-1">Will be shown to attendees on event detail page</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Capacity</label>
              <input type="number" name="capacity" value={formData.capacity} onChange={handleChange} min="1"
                placeholder="Leave empty for unlimited"
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
              <p className="text-xs text-[#4C5A70] mt-1">Leave blank for unlimited attendees</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Blocked Seats</label>
              <input type="number" name="blocked_seats" value={formData.blocked_seats} onChange={handleChange} min="0"
                placeholder="0"
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
              <p className="text-xs text-[#4C5A70] mt-1">🔒 Reserved for VIPs/staff (hidden from public)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Max Guests Per RSVP</label>
              <select name="max_guests_per_rsvp" value={formData.max_guests_per_rsvp || 1} onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
                <option value="0">No Guests Allowed</option>
                <option value="1">Allow +1 Guest</option>
                <option value="2">Allow +2 Guests</option>
                <option value="3">Allow +3 Guests</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="requires_rsvp" checked={formData.requires_rsvp} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <span className="text-sm font-medium text-[#0B2240]">Requires RSVP</span>
              </label>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="allow_guests" checked={formData.allow_guests !== false} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <span className="text-sm font-medium text-[#0B2240]">Allow Guests</span>
              </label>
            </div>
          </div>

          {/* Donation Link Section */}
          <div className="border-t border-[#E1DFD7] pt-6">
            <div className="flex items-center gap-2 mb-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="enable_donation_link" checked={formData.enable_donation_link} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <span className="text-sm font-medium text-[#0B2240]">Enable Donation Link</span>
              </label>
            </div>
            
            {formData.enable_donation_link && (
              <div className="space-y-4 bg-[#F7F6F2] p-4 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-[#0B2240] mb-2">Donation Link URL *</label>
                  <input type="url" name="donation_link_url" value={formData.donation_link_url} onChange={handleChange}
                    placeholder="https://give.kgob.com/charity-event"
                    required={formData.enable_donation_link}
                    className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
                  <p className="text-xs text-[#4C5A70] mt-1">Full URL to external donation page</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B2240] mb-2">Button Text</label>
                  <input type="text" name="donation_link_text" value={formData.donation_link_text} onChange={handleChange}
                    placeholder="Support This Cause"
                    className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
                  <p className="text-xs text-[#4C5A70] mt-1">Text displayed on the donation button</p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-[#C7362E]/10 border border-[#C7362E] rounded-lg p-4">
              <p className="text-[#C7362E] text-sm">{error}</p>
            </div>
          )}
          </div>

          {/* Footer - Fixed */}
          <div className="px-8 py-4 border-t border-[#E1DFD7] bg-[#F7F6F2]">
            <div className="flex gap-3">
              <button type="submit" disabled={loading}
                className="flex-1 h-12 bg-[#0B2240] text-white rounded-lg font-semibold hover:bg-[#0A1D35] disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Event'}
              </button>
              <button type="button" onClick={onCancel}
                className="px-6 h-12 border border-[#E1DFD7] text-[#0B2240] rounded-lg hover:bg-[#EDEBE6]">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
